// web/src/contact/TurnstileWidget.tsx
//
// Thin wrapper around @marsidev/react-turnstile. Exposes an imperative
// reset() handle so the form can clear the widget after a 422
// captcha_failed response from the backend (the user needs to solve a
// fresh challenge before resubmitting).
import { Turnstile } from "@marsidev/react-turnstile";
import { forwardRef, useImperativeHandle, useRef } from "react";

export type TurnstileWidgetHandle = {
  reset: () => void;
};

type Props = {
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
};

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, Props>(
  function TurnstileWidget({ onVerify, onError, onExpire }, ref) {
    const innerRef = useRef<{ reset: () => void } | null>(null);

    useImperativeHandle(ref, () => ({
      reset: () => innerRef.current?.reset(),
    }));

    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
    if (!siteKey) {
      // Defensive: should never happen since .env sets the always-passes key.
      // Render nothing rather than crash; the form will still work in dev.
      return null;
    }

    return (
      <Turnstile
        ref={innerRef as never}
        siteKey={siteKey}
        onSuccess={onVerify}
        onError={onError}
        onExpire={onExpire}
        options={{ theme: "auto" }}
      />
    );
  },
);
